import { useEffect, useState, useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { currentCulture } from 'application/adapters/store/slices/cultureSlice';
import { LocaleContext } from 'application/adapters/context/LocaleContext';
import { AcademySearchResults } from './sections/AcademySearchResultsList';
import { AcademyDetailsSection } from './sections/AcademyDetailsSection';
import { Container, PageSpinner } from 'ui/components';
import { getAcademyEntryDetailById } from 'application/repositories/academyRepository';

export const AcademyPageFeature = ({ content }) => {
	const router = useRouter();
	const culture = useSelector(currentCulture);
	const localeContext = useContext(LocaleContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [academyEntry, setAcademyEntry] = useState<Models.AcademyEntry | null>(null);

	const linkToAcademyEntryDetails = useCallback(
		(item: Models.AcademyEntry) => {
			if (!item?.id) return undefined;
			const basePath = router.asPath.split('?')[0]; // keep locale, drop old query
			return {
				linkType: 'internal',
				url: `${basePath}?id=${encodeURIComponent(item?.id)}`,
				name: item?.title || '',
				target: '_self',
			};
		},
		[router.asPath],
	);

	// Fetch entry details whenever the query id changes (e.g. navigation via link or browser back/forward).
	useEffect(() => {
		let cancelled = false;
		const ac = new AbortController();

		async function loadDetailByUrlId() {
			const id = router.query.id as string | undefined;

			if (!id) {
				// Leaving detail mode
				setAcademyEntry(null);
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				const data = await getAcademyEntryDetailById(id, (router.query.host as string) || '');
				if (!cancelled) setAcademyEntry(data);
			} catch (err) {
				if (!cancelled) {
					console.error('Failed to load entry', err);
					setAcademyEntry(null);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void loadDetailByUrlId();
		return () => {
			cancelled = true;
			ac.abort();
		};
	}, [router.query.id, router.query.host]);

	return (
		<>
			{isLoading && <PageSpinner />}
			{!isLoading && academyEntry ? (
				<AcademyDetailsSection content={content} academyEntry={academyEntry} id={academyEntry.id} host={router.query.host as string} />
			) : (
				<AcademySearchResults content={content} culture={culture} localeContext={localeContext} linkToEntry={linkToAcademyEntryDetails} />
			)}
		</>
	);
};
